import React from 'react';
import { useLocalSearchParams } from 'expo-router';

import { useIsFocused } from '@sg/garnish';

import {
  MenuContent,
  useDeliveryMenuContentData,
  useMenuContent,
  useProductDetailsContentData,
} from '../../components';

// ─────────────────────────────────────────────────────────────────────────────

export const DeliveryMenuScreen = () => {
  //
  // ─── Navigation ──────────────────────────────────────────────────────

  const { addressId } = useLocalSearchParams<{ addressId: string }>();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { restaurantData, deliveryDetailsData, isFetching, hasError, hasNoProducts } =
    useDeliveryMenuContentData({ addressId });

  const { productDetailsContentData } = useProductDetailsContentData();
  const { mappedContent } = useMenuContent();

  const { categoriesBadges, caloriesOverrides } = mappedContent;

  // ─────────────────────────────────────────────────────────────────────

  const isScreenFocused = useIsFocused();
  const shouldRenderErrorView =
    hasError || !restaurantData || !deliveryDetailsData || hasNoProducts;

  // When the menu screen is rendered without being focused, there won't be any data available.
  const isWaitingForFocus = !isScreenFocused && shouldRenderErrorView;

  if (isFetching || isWaitingForFocus) {
    return <MenuContent.Loading />;
  }

  if (shouldRenderErrorView) {
    return <MenuContent.Error />;
  }

  // ─────────────────────────────────────────────────────────────────────

  const locationName = deliveryDetailsData.addressName ?? deliveryDetailsData?.shortAddress;

  return (
    <MenuContent.Ready
      // NOTE: We use the address ID as the key to force a re-render when the
      //       delivery details data changes to prevent having a stale data and components.
      key={deliveryDetailsData.addressId}
      // @ts-expect-error - restaurantData missing sanitaryGradeDisplayText/sanitaryGradeUrl
      restaurantData={restaurantData}
      productDetailsContentData={productDetailsContentData}
      categoriesBadges={categoriesBadges}
      caloriesOverrides={caloriesOverrides}
      deliveryDetailsData={deliveryDetailsData}
      orderChannel="delivery"
      locationName={locationName}
    />
  );
};
